import { getErrorFromResponse } from '@atlassian/jira-common-directory-v2/src/rest/errors.tsx';
import {
	ITEMS_ON_PAGE,
	START_AT,
	ORDER,
	FILTER,
} from '../../common/constants/default-parameters.tsx';
import type { Component } from '../../common/model/types.tsx';
import { createOptions } from '../../common/rest/index.tsx';
import { mapToOrderFetch } from '../../query-param-adapter/model/constants/order-field-mapper.tsx';
import type { Query } from '../../query-param-adapter/model/index.tsx';
import { getErrorFromResultJson } from '../errors.tsx';
import type { State } from '../reducer.tsx';
import { transformComponents, transformComponentObj } from './transformers.tsx';

export const createRetrieveUrl = (
	projectKey: string,
	startAt: number,
	maxResults: number,
	orderBy: string,
	filter: string,
) => {
	const encodedQuery: string = encodeURIComponent(filter);
	const componentSourceParam = '&componentSource=auto';
	return `/rest/api/2/project/${projectKey}/component?startAt=${startAt}&maxResults=${maxResults}&orderBy=${orderBy}&query=${encodedQuery}${componentSourceParam}`;
};

export type RequestResult = {
	byId: {
		[key: string]: Component;
	};
	list: string[];
	listAri: string[];
	total: number;
};

export const requestComponents = async (
	projectKey: string,
	startAt: number = START_AT,
	maxResults: number = ITEMS_ON_PAGE,
	orderBy: string = ORDER,
	filter: string = FILTER,
): Promise<RequestResult> => {
	const fullUrl = createRetrieveUrl(projectKey, startAt, maxResults, orderBy, filter);
	const response = await fetch(fullUrl, createOptions('GET'));
	const json = await response.json();

	if (!response.ok) {
		throw await getErrorFromResponse(response, 'Failed to fetch');
	}

	const { values, total } = json;
	return transformComponents(values, total);
};

export const getPrefetchedData = (projectKey: string, query?: Query): Promise<RequestResult> => {
	if (query) {
		const { orderDirection, orderField, filter, page } = query;
		const orderBy = mapToOrderFetch(orderField, orderDirection);
		const maxResults = ITEMS_ON_PAGE;
		const startAt = (page - 1) * maxResults;

		return requestComponents(projectKey, startAt, maxResults, orderBy, filter);
	}
	return requestComponents(projectKey);
};

export const createRetrieveOneUrl = (id: string) => `/rest/api/2/component/${id}`;

type RequestComponent = (
	state: State,
	id: string,
) => Promise<{
	component: Component;
	id: string;
}>;

export const requestComponent: RequestComponent = async (state, id) => {
	const fullUrl = `${createRetrieveOneUrl(id)}`;
	const response = await fetch(fullUrl, createOptions('GET'));
	const json = await response.json();

	if (response.ok) {
		return {
			id,
			component: transformComponentObj(json),
		};
	}

	throw getErrorFromResultJson(json, response.status);
};
